// Minimal markdown parser for assistant chat answers — zero dependencies
// (connectivity at sea is poor; a full markdown library is dead weight for
// the handful of constructs the model actually emits).
//
// SECURITY MODEL: the input is untrusted model output. The parser emits
// plain data (block/inline token objects) — never HTML strings — and
// MarkdownMessage.jsx turns those tokens into React elements whose text
// content React escapes on render. There is exactly ONE escape/encode pass
// (React's), so raw HTML in an answer (<script>, <img onerror>, …) shows up
// as literal text and is never parsed as markup.
//
// Entities: answers sometimes arrive with HTML escaping already applied
// ("P&amp;ID" for "P&ID"). decodeEntitiesOnce runs exactly ONE decode pass
// before parsing so the visible text ends up escaped a single time —
// "&amp;amp;" decodes to the literal "&amp;", never all the way to "&".

const NAMED_ENTITIES = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

export function decodeEntitiesOnce(text) {
  return String(text ?? '').replace(/&(#\d+|#[xX][0-9a-fA-F]+|[a-zA-Z]+);/g, (match, body) => {
    if (body[0] === '#') {
      const code =
        body[1] === 'x' || body[1] === 'X'
          ? parseInt(body.slice(2), 16)
          : parseInt(body.slice(1), 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
    }
    const named = NAMED_ENTITIES[body.toLowerCase()];
    return named !== undefined ? named : match;
  });
}

// Inline constructs: `code`, **bold**, *italic*, _italic_. No nesting —
// emphasis content is kept as plain text. The lookarounds keep intra-word
// underscores (file_name_here) and stray asterisks (5 * 6) as literal text.
const INLINE_RE = new RegExp(
  [
    '`([^`\\n]+)`', // 1: inline code
    '\\*\\*(.+?)\\*\\*', // 2: bold
    '(?<![\\w*])\\*([^\\s*](?:[^*\\n]*[^\\s*])?)\\*(?![\\w*])', // 3: *italic*
    '(?<!\\w)_([^\\s_](?:[^_\\n]*[^\\s_])?)_(?!\\w)', // 4: _italic_
  ].join('|'),
  'g'
);

// -> [{type: 'text'|'code'|'bold'|'italic', content: string}, …]
export function parseInline(text) {
  const s = String(text ?? '');
  const tokens = [];
  let last = 0;
  for (const m of s.matchAll(INLINE_RE)) {
    if (m.index > last) tokens.push({ type: 'text', content: s.slice(last, m.index) });
    if (m[1] !== undefined) tokens.push({ type: 'code', content: m[1] });
    else if (m[2] !== undefined) tokens.push({ type: 'bold', content: m[2] });
    else tokens.push({ type: 'italic', content: m[3] !== undefined ? m[3] : m[4] });
    last = m.index + m[0].length;
  }
  if (last < s.length) tokens.push({ type: 'text', content: s.slice(last) });
  return tokens;
}

const HEADING_RE = /^(#{1,4})\s+(.*)$/;
const BULLET_RE = /^\s*[-*•]\s+(.*)$/;
const ORDERED_RE = /^\s*(\d+)[.)]\s+(.*)$/;
const HR_RE = /^\s*([-*_])\s*(?:\1\s*){2,}$/;
const FENCE_RE = /^\s*```/;

function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map(c => c.trim());
}

// A GFM table separator row: |---|:---:|--- (pipes required somewhere).
function isTableSeparator(line) {
  if (typeof line !== 'string' || !line.includes('|')) return false;
  const cells = splitRow(line);
  return cells.length > 0 && cells.every(c => /^:?-+:?$/.test(c));
}

// -> array of blocks:
//   {type:'heading', level, inlines}   {type:'p', lines: [inlines, …]}
//   {type:'ul', items: [inlines, …]}   {type:'ol', start, items}
//   {type:'table', header, rows}       {type:'code', content}
//   {type:'hr'}
export function parseMarkdown(raw) {
  const lines = decodeEntitiesOnce(raw).split(/\r?\n/);
  const blocks = [];
  let i = 0;

  const isTableStart = idx => lines[idx].includes('|') && isTableSeparator(lines[idx + 1]);
  const startsBlock = idx =>
    FENCE_RE.test(lines[idx]) ||
    HEADING_RE.test(lines[idx]) ||
    HR_RE.test(lines[idx]) ||
    BULLET_RE.test(lines[idx]) ||
    ORDERED_RE.test(lines[idx]) ||
    isTableStart(idx);

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }

    if (FENCE_RE.test(line)) {
      const buf = [];
      i += 1;
      while (i < lines.length && !FENCE_RE.test(lines[i])) {
        buf.push(lines[i]);
        i += 1;
      }
      i += 1; // closing fence (or EOF)
      blocks.push({ type: 'code', content: buf.join('\n') });
      continue;
    }

    const heading = line.match(HEADING_RE);
    if (heading) {
      blocks.push({
        type: 'heading',
        level: heading[1].length,
        inlines: parseInline(heading[2].trim()),
      });
      i += 1;
      continue;
    }

    if (isTableStart(i)) {
      const header = splitRow(line).map(parseInline);
      i += 2; // header + separator
      const rows = [];
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
        rows.push(splitRow(lines[i]).map(parseInline));
        i += 1;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    if (HR_RE.test(line)) {
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    if (BULLET_RE.test(line)) {
      const items = [];
      while (i < lines.length && BULLET_RE.test(lines[i]) && !HR_RE.test(lines[i])) {
        items.push(parseInline(lines[i].match(BULLET_RE)[1]));
        i += 1;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    const ordered = line.match(ORDERED_RE);
    if (ordered) {
      const start = parseInt(ordered[1], 10);
      const items = [];
      while (i < lines.length && ORDERED_RE.test(lines[i])) {
        items.push(parseInline(lines[i].match(ORDERED_RE)[2]));
        i += 1;
      }
      blocks.push({ type: 'ol', start, items });
      continue;
    }

    // Paragraph: consecutive plain lines. Each line keeps its own inline
    // token list so the renderer can place <br> between them — the bubble
    // no longer relies on white-space: pre-wrap for assistant messages.
    const para = [];
    do {
      para.push(parseInline(lines[i].trim()));
      i += 1;
    } while (i < lines.length && lines[i].trim() && !startsBlock(i));
    blocks.push({ type: 'p', lines: para });
  }

  return blocks;
}
